<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="700"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
    @click:outside="!showSummary && closeDialog()"
    @keydown.esc="showSummary ? goBack() : closeDialog()"
    @shortkey.native="showSummary ? goBack() : closeDialog()"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">Return Loan</span>
          <VSpacer />
          <span class="text--primary headline-2">{{ securityDescription }}</span>
        </VCardTitle>

        <VCardText :class="{ 'show-summary': showSummary }">
          <!-- Input fields -->
          <VContainer v-if="!showSummary" class="form-input pl-12">
            <VRow align="center">
              <VCol class="col-4 py-1 text-capitalize"> counterparty</VCol>
              <VCol class="py-0">
                {{ loan.counterpartyDisplay }}
              </VCol>
            </VRow>

            <VRow align="center">
              <VCol class="col-4 py-1 text-capitalize"> current rate</VCol>
              <VCol class="py-0">
                <RateOutput :rate="loan.rate" :rate-modifier="loan.rateModifier" />
              </VCol>
            </VRow>

            <VRow align="center">
              <VCol class="col-4 py-1 text-capitalize">
                {{ $t('Open Quantity') }}
              </VCol>
              <VCol class="py-0"><PrettyNumber :value="loan.openQuantity" /></VCol>
            </VRow>

            <VRow v-if="hasPendingRecall" align="center" class="red--text pt-6">
              <VCol class="col-4 py-1 text-capitalize"> recalled quantity</VCol>
              <VCol class="py-0">
                <PrettyNumber :value="loan.recalledQuantity" />
              </VCol>
            </VRow>

            <VRow v-if="loan.pendingReturnQuantity > 0" align="center" class="orange--text">
              <VCol class="col-4 py-1 text-capitalize"> pending return</VCol>
              <VCol class="py-0">
                <PrettyNumber :value="loan.pendingReturnQuantity" />
              </VCol>
            </VRow>
            <VRow align="center" class="pt-6">
              <VCol class="col-4 py-1 text-capitalize"> return quantity</VCol>
              <VCol class="py-0">
                <NumericInput
                  v-model="quantity"
                  autofocus
                  data-test="quantity-input"
                  :error-messages="errorMsgs.quantity"
                  label="quantity"
                  :max="loan.openQuantityToReturn"
                  :min="0"
                  placeholder="0"
                  :step="100"
                  type="integer"
                  @blur="touched = true"
                  @input="touched = true"
                >
                  <template #append>
                    <VBtn
                      class="set-max pa-0"
                      data-test="max-btn"
                      outlined
                      x-small
                      @click="setQuantityToMax()"
                    >
                      Max
                    </VBtn>
                  </template>
                </NumericInput>
              </VCol>
            </VRow>
            <VAlert
              v-if="isAfterCutOffTime(loan) && loan.counterparty.cutOffTime"
              class="mt-8"
              outlined
              type="warning"
            >
              Return Cutoff Time: {{ formatAmPmTime(loan.counterparty.cutOffTime) }}
              {{ clientConfig?.timezoneLabel }}. Any returns initiated after this time will require
              the lender to approve the return before they get officially recorded to the trade
              and/or settlement instructions sent.
            </VAlert>
          </VContainer>

          <!-- Form summary -->
          <VContainer
            v-if="showSummary"
            v-shortkey="['enter']"
            class="form-summary"
            @shortkey="submitForm()"
          >
            <VRow v-if="quantity !== null" class="text--primary">
              <VCol class="py-0">
                <h2 class="text-h6 text--secondary">Continue?</h2>
                <div v-if="quantity === loan.openQuantity">
                  You are about to return all <b>{{ quantity }}</b> shares of this loan.
                </div>
                <div
                  v-else-if="
                    hasPendingRecall &&
                    loan.recalledQuantity > quantity + loan.pendingReturnQuantity
                  "
                >
                  You are about to return <b>{{ quantity }}</b> shares of this loan.
                  <br />
                  This will <b>not</b> fulfill the current recall.
                </div>
                <div v-else>
                  You are about to return <b>{{ quantity }}</b> of the
                  {{ loan.openQuantity }} shares in this loan.
                </div>
              </VCol>
            </VRow>

            <div class="py-8 pl-10">
              <VRow align="center">
                <VCol class="col-4 py-1 text-capitalize"> Counterparty</VCol>
                <VCol class="py-0">
                  {{ loan.counterpartyDisplay }}
                </VCol>
              </VRow>

              <VRow align="center">
                <VCol class="col-4 py-1 text-capitalize"> Current Rate</VCol>
                <VCol class="py-0">
                  <RateOutput :rate="loan.rate" :rate-modifier="loan.rateModifier" />
                </VCol>
              </VRow>

              <VRow align="center">
                <VCol class="col-4 py-1 text-capitalize">
                  {{ $t('Open Quantity') }}
                </VCol>
                <VCol class="py-0">
                  <PrettyNumber :value="loan.openQuantity" />
                </VCol>
              </VRow>

              <VRow v-if="hasPendingRecall" align="center" class="red--text">
                <VCol class="col-4 py-1 text-capitalize"> recalled quantity</VCol>
                <VCol class="py-0">
                  <PrettyNumber :value="loan.recalledQuantity" />
                </VCol>
              </VRow>
              <VRow v-if="loan.pendingReturnQuantity > 0" align="center" class="orange--text">
                <VCol class="col-4 py-1 text-capitalize"> pending return</VCol>
                <VCol class="py-0">
                  <PrettyNumber :value="loan.pendingReturnQuantity" />
                </VCol>
              </VRow>

              <VRow align="center" class="primary--text">
                <VCol class="col-4 py-1 text-capitalize"> to return</VCol>
                <VCol class="py-0">
                  <PrettyNumber :value="quantity" />
                </VCol>
              </VRow>
            </div>

            <VRow>
              <VCol>
                <VDivider />
              </VCol>
            </VRow>

            <VRow v-if="showError || showSuccess">
              <VCol class="pa-0 px-1 col-6 offset-3">
                <div
                  class="v-alert v-alert--dense text--primary text-body-2 text-center"
                  :class="{ error: showError, success: showSuccess }"
                >
                  <div v-if="showError">
                    {{ errorMsgs.apiErrors.join('\n') }}
                  </div>
                  <div v-if="showSuccess">Your return has been sent.</div>
                </div>
              </VCol>
            </VRow>
          </VContainer>
        </VCardText>

        <!-- Dialog box actions -->
        <VCardActions class="d-flex">
          <div v-if="!showSummary" class="d-flex flex-grow-1 justify-space-between align-end">
            <VBtn color="secondary" data-test="cancel-btn" @click="closeDialog">
              {{ backButtonText }}
            </VBtn>
            <VBtn color="primary" data-test="return-btn" type="submit" @click="goToSummary()">
              Return</VBtn
            >
          </div>
          <div v-if="showSummary" class="d-flex flex-grow-1 justify-space-between align-end">
            <VBtn
              color="secondary"
              data-test="back-btn"
              :disabled="formStatus !== 'idle'"
              @click="goBack()"
            >
              {{ backButtonText }}
            </VBtn>

            <VBtn
              color="primary"
              data-test="confirm-btn"
              :disabled="formStatus !== 'idle'"
              :loading="formStatus === 'submitting'"
              type="submit"
              @click="submitForm()"
            >
              {{ $t('dialogs.confirmButton') }}
            </VBtn>
          </div>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue';
import type { PropType } from 'vue';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import wait from '@/modules/common/services/wait';
import { ApiError } from '@/utils/errors';
import type { DialogFormStatus } from '@/modules/common/types/dialog';
import { formatAmPmTime } from '@/utils/helpers/time';
import type { BorrowerOpenLoan, LenderOpenLoan, OpenLoan } from '@/modules/common/models';
import { isAfterCutOffTime as isAfterCutOff, useCurrentTime } from '@/composables/current-time';
import { store as vuex } from '@/store/store';

interface FormErrors {
  apiErrors: string[];
  quantity: string[];
}

const props = defineProps({
  loan: {
    type: Object as PropType<LenderOpenLoan | BorrowerOpenLoan>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'close-modal'): void;
}>();

const { currentTimeUTC } = useCurrentTime();
const clientConfig = computed(() => vuex.state.clientConfig);
const timezoneOffset = computed(() => vuex.state.clientConfig?.timezoneOffset);

const instance = getCurrentInstance()!;

const quantity = ref<number | null>(null);
const showSummary = ref(false);
const formStatus = ref<DialogFormStatus>('idle');
const showSuccess = ref(false);
const apiErrors = ref<string[]>([]);
const touched = ref(false);

function isAfterCutOffTime(loan: OpenLoan): boolean {
  return isAfterCutOff(loan, currentTimeUTC.value, timezoneOffset.value);
}

const errorMsgs = computed<FormErrors>(() => {
  const errors: FormErrors = {
    apiErrors: apiErrors.value,
    quantity: [],
  };

  if (touched.value) {
    const q = quantity.value;
    if (q === null || q === undefined) errors.quantity.push('please enter a quantity.');
    else if (!Number.isInteger(q)) errors.quantity.push('please enter a valid quantity.');
    else if (q < 1) errors.quantity.push('must be greater than 0.');
    else if (q > props.loan.openQuantity)
      errors.quantity.push('cannot return more than the open quantity');
    else if (q > props.loan.openQuantityToReturn)
      errors.quantity.push('cannot return this amount due to a pending return');
  }

  return errors;
});

const showError = computed(() => apiErrors.value.length > 0);

const securityDescription = computed(
  () => `${props.loan.security.name} / ${props.loan.security.ticker} [${props.loan.security.cusip}]`
);

const hasPendingRecall = computed(() => props.loan.recalledQuantity > 0);

const backButtonText = computed(() => {
  if (showSummary.value) {
    return instance.proxy!.$t('dialogs.goBackButton') as string;
  }
  return instance.proxy!.$t('dialogs.cancelButton') as string;
});

// put any recalled amount in by default, taking into account pending returns
if (props.loan.recalledQuantity > 0) {
  quantity.value = Math.max(0, props.loan.recalledQuantity - props.loan.pendingReturnQuantity);
}

function setQuantityToMax(): void {
  quantity.value = props.loan.openQuantityToReturn;
}

function validateForm(): boolean {
  touched.value = true;
  apiErrors.value = [];
  return errorMsgs.value.quantity.length === 0;
}

function goToSummary(): void {
  showSummary.value = validateForm();
}

function goBack(): void {
  showSummary.value = false;
}

async function submitForm(): Promise<void> {
  if (formStatus.value !== 'idle' || !validateForm()) {
    return;
  }
  formStatus.value = 'submitting';

  try {
    const waitTimer = wait(800);
    await instance.proxy!.$api.openLoans.postReturn(props.loan, quantity.value as number);

    // success, alert user and then close form
    showSuccess.value = true;
    formStatus.value = 'closing';

    // wait a minimum amount of time before closing the dialog so that the UI nicely shows the success message
    await waitTimer;
    closeDialog();
  } catch (err) {
    const errorMessage = new ApiError(i18nServerMessage(err as Error)).message;
    apiErrors.value = [errorMessage];
  } finally {
    formStatus.value = 'idle';
  }
}

function closeDialog(): void {
  emit('close-modal');
}

defineExpose({
  test: {
    quantity,
    showSummary,
    formStatus,
  },
});
</script>

<style lang="scss" scoped>
.v-card__text {
  min-height: 15rem;
  max-height: 20rem;
  transition:
    min-height 250ms linear,
    max-height 250ms linear;

  &.show-summary {
    min-height: 16rem;
    max-height: 22rem;
  }
}

.form-input {
  .v-btn.set-max {
    height: 1.5rem;
  }
}

// General Transitions and Animations
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
